import React, { useState, useEffect } from 'react'
import { CheckCircle2, ChevronDown, Phone, XCircle } from 'lucide-react'
import Flag from 'react-world-flags'
import { useTranslation } from 'react-i18next'

interface PhoneInputProps {
  value?: string
  onChange?: (value: string) => void
  onCountryChange?: (value: string) => void
  error?: string
  isInvalid?: boolean
  isPhoneConfirmed?: boolean
  className?: string // Ajout du className pour le styling
}

const countries = [
  { value: 'BE', labelKey: 'belgium', prefix: '+32' },
  { value: 'FR', labelKey: 'france', prefix: '+33' },
  { value: 'DE', labelKey: 'germany', prefix: '+49' },
  { value: 'LU', labelKey: 'luxembourg', prefix: '+352' },
  { value: 'NL', labelKey: 'netherlands', prefix: '+31' },
]

const validatePhoneNumber = (
  phoneNumber: string,
  countryCode: string,
): boolean => {
  const patterns: Record<string, RegExp> = {
    BE: /^(?:\+32|0)[1-9]\d{7,8}$/,
    FR: /^(?:\+33|0)[67]\d{8}$/,
    DE: /^(?:\+49|0)[1-9]\d{8,12}$/,
    LU: /^(?:\+352)[0-9]{6,10}$/,
    NL: /^(?:\+31|0)[1-9]\d{8}$/,
  }
  return patterns[countryCode]?.test(phoneNumber) || false
}

const detectCountryFromPhone = (phone: string) => {
  return (
    countries.find(country => phone.startsWith(country.prefix))?.value ||
    countries[0].value
  )
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  value = '',
  onChange = () => {},
  onCountryChange = () => {},
  error,
  isInvalid = false,
  isPhoneConfirmed = false,
  className = '', // Permet d'appliquer un style conditionnel
}) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState(value)
  const [selectedCountry, setSelectedCountry] = useState<
    (typeof countries)[0] | null
  >(null)

  useEffect(() => {
    if (value) {
      setPhoneNumber(value)
      const detectedCountry = detectCountryFromPhone(value)
      const country = countries.find(c => c.value === detectedCountry)
      if (country) {
        setSelectedCountry(country)
      }
    }
  }, [value])

  useEffect(() => {
    setPhoneNumber(value)
  }, [value])

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value
    setPhoneNumber(input)
    onChange(input)

    const detectedCountry = detectCountryFromPhone(input)
    if (selectedCountry && detectedCountry !== selectedCountry.value) {
      onCountryChange(detectedCountry)
      const country = countries.find(c => c.value === detectedCountry)
      if (country) {
        setSelectedCountry(country)
      }
    }
  }

  const handleCountrySelect = (country: (typeof countries)[0]) => {
    setSelectedCountry(country)
    setIsOpen(false)
    onCountryChange(country.value)

    const newValue = country.prefix
    setPhoneNumber(newValue)
    onChange(newValue)
  }

  const isNumberInvalid =
    isInvalid ||
    (phoneNumber !== '' &&
      selectedCountry !== null &&
      !validatePhoneNumber(phoneNumber, selectedCountry.value))

  return (
    <div className={`relative ${className}`}>
      <div className={`flex rounded-md py-0 transition-colors duration-300`}>
        <button
          type='button'
          onClick={() => setIsOpen(!isOpen)}
          className='flex items-center px-3 border-r border-anthracite/10 bg-grisclair rounded-l-md hover:bg-grisclair2'
        >
          {selectedCountry ? (
            <>
              <Flag
                code={selectedCountry.value.toLowerCase()}
                className='w-6 h-4 mr-2'
              />
              <span className='text-sm text-gray-600'>
                {selectedCountry.prefix}
              </span>
            </>
          ) : (
            <span className='text-sm text-gray-600'>{t('select-country')}</span>
          )}
          <ChevronDown className='w-4 h-4 ml-1 text-gray-400' />
        </button>

        <div className='relative flex-1'>
          <Phone className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
          <div className={`relative ${className}`}>
            <input
              type='tel'
              value={phoneNumber}
              onChange={e => {
                setPhoneNumber(e.target.value)
                onChange(e.target.value)
              }}
              className={`w-full px-3 py-2 rounded-md transition-colors duration-300 focus:outline-none ${
                className ? className : 'bg-grisclair'
              }`}
              placeholder='XX XX XX XX XX'
            />
          </div>
          {phoneNumber && (
            <div className='absolute right-3 top-1/2 transform -translate-y-1/2'>
              {isPhoneConfirmed ? (
                <CheckCircle2 className='w-4 h-4 text-green-500' />
              ) : (
                <XCircle className='w-4 h-4 text-redpink/70' />
              )}
            </div>
          )}
        </div>
      </div>

      {isNumberInvalid && error && (
        <p className='mt-2 text-sm text-redpink'>{error}</p>
      )}

      {isOpen && (
        <div className='absolute z-10 w-full mt-1 bg-grisclair border rounded-md shadow-lg'>
          {countries.map(country => (
            <button
              key={country.value}
              onClick={() => handleCountrySelect(country)}
              className='flex items-center w-full px-4 py-2 hover:bg-gray-100'
            >
              <Flag
                code={country.value.toLowerCase()}
                className='w-6 h-4 mr-2'
              />
              <span className='text-sm'>
                {t(`countries.${country.labelKey}`)} ({country.prefix})
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default PhoneInput
